import { ConfirmationModal } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

export type PlanChangeDirection = 'upgrade' | 'downgrade';

interface ChangePlanModalProps {
  open: boolean;
  loading: boolean;
  direction: PlanChangeDirection;
  newPlanName: string;
  /** Activation date for downgrade flow. Ignored when direction is upgrade. */
  periodEndLabel: string | null;
  /** An upgrade within credit plans: Stripe applies it once its invoice is paid. */
  paidFirst?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function ChangePlanModal({
  open,
  loading,
  direction,
  newPlanName,
  periodEndLabel,
  paidFirst = false,
  onConfirm,
  onClose
}: ChangePlanModalProps) {
  const t = useFeatureTranslation('systemSettings.payments');
  const tCredits = useFeatureTranslation('credits.billing');

  const title = t(`changePlan.${direction}.title`, { plan: newPlanName });
  const message =
    direction === 'upgrade'
      ? // A paid-first upgrade does not apply now, so the immediate-upgrade copy would be wrong.
        paidFirst
        ? tCredits('upgradePaidFirst')
        : t('changePlan.upgrade.body', { plan: newPlanName })
      : periodEndLabel
        ? t('changePlan.downgrade.bodyWithDate', { plan: newPlanName, date: periodEndLabel })
        : t('changePlan.downgrade.bodyWithoutDate', { plan: newPlanName });
  const confirmLabel = t(`changePlan.${direction}.confirm`);
  const dismissLabel = t('changePlan.dismiss');

  return (
    <ConfirmationModal
      open={open}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={dismissLabel}
      variant={direction === 'upgrade' ? 'primary' : 'warning'}
      loading={loading}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}
